// NOTE: hooks
import { toast } from 'sonner'
import { useEffect, useState } from 'react'
import useProducts from './useProducts'
import { useSearchParams } from 'react-router-dom'
// NOTE: Components
import ProductCard from './ProductCard'
import Loading from '../ui/Loading'
import { Carousel, SwiperSlide } from '../ui/carousel/Carousel'
import { MdOutlineRemoveShoppingCart as ShopIcon } from 'react-icons/md'

const ProductList = () => {
    const {
        isLoadingProducts,
        errorProducts,
        isErrorProducts,
        isLoadingCollections,
        isErrorCollections,
        errorCollections,
        products,
        collections
    } = useProducts()
    const [urlParams, setUrlParams] = useSearchParams()
    const [filteredProducts, setFilteredProducts] = useState<any>([])
    const [selectedCollection, setSelectedCollection] = useState<string>('all')

    const extendedCollections = [
        'all',
        ...(collections?.map((collection: any) => collection.node?.title) || []),
        'others'
    ]

    const filterProducts = (collection: string) => {
        if (!products) return

        if (collection === 'all') return setFilteredProducts(products)

        if (collection === 'others') {
            const othersProducts = products.filter(
                (product: any) =>
                    !product?.node?.collections?.edges?.some((edge: any) =>
                        extendedCollections.includes(edge?.node?.title)
                    )
            )

            return setFilteredProducts(othersProducts)
        }

        const filtered = products.filter((product: any) =>
            product?.node?.collections?.edges.some((edge: any) => edge?.node?.title === collection)
        )

        setFilteredProducts(filtered)
    }

    const changeCollections = (collection: string) => {
        setUrlParams({ collection })
        setSelectedCollection(collection)
        filterProducts(collection)
    }

    useEffect(() => {
        const urlCollection = urlParams.get('collection') ?? 'all'
        if (extendedCollections.includes(urlCollection)) {
            setSelectedCollection(urlCollection)
        }

        filterProducts(selectedCollection)
    }, [collections, urlParams, selectedCollection, products])

    if (isLoadingProducts || isLoadingCollections) return <Loading />

    if (isErrorProducts) toast.error("Couldn't get products", { description: errorProducts?.message })
    if (isErrorCollections) toast.error("Couldn't get collections", { description: errorCollections?.message })

    return (
        <div>
            <div className="mb-6">
                <Carousel>
                    {extendedCollections.map((collection, index) => (
                        <SwiperSlide key={index} className="w-fit min-w-fit">
                            <button
                                className={`mr-2 flex w-fit rounded-full px-5 py-2 text-center font-semibold capitalize shadow-sm md:mr-4 ${selectedCollection === collection
                                    ? 'text-dark hover:text-dark bg-gradient-to-r from-clay-100 to-orange-100'
                                    : 'bg-gradient-to-r from-[#f0f0fa] to-[#e8f2f4] text-green-900 hover:text-green-900'
                                    }`}
                                onClick={() => changeCollections(collection)}
                            >
                                {collection}
                            </button>
                        </SwiperSlide>
                    ))}
                </Carousel>
            </div>


            {(!filteredProducts || filteredProducts.length === 0) && (
                <>
                    <ShopIcon className="mx-auto mt-4 size-16 text-slate-400" />

                    <p className="mb-12 text-center text-lg font-semibold text-slate-500">
                        No products found
                    </p>
                </>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredProducts?.map(
                    ({
                        node: {
                            id,
                            images,
                            handle,
                            vendor,
                            title,
                            variants,
                            descriptionHtml
                        }
                    }: any) => (
                        <ProductCard
                            key={id}
                            images={images}
                            vendor={vendor}
                            title={title}
                            description={descriptionHtml}
                            handle={handle}
                            variants={variants}
                            className="mb-10 sm:mb-6"
                            skeletonClassName="h-[300px]"
                        />
                    )
                )}
            </div>
        </div>
    )
}

export default ProductList